import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { Phone, Users } from "lucide-react";

const gd = (id) => `https://lh3.googleusercontent.com/d/${id}`;

const TEAM = [
  {
    key: "nagesh",
    name: "Dr. K. S. Nagesh",
    role: "Chief Advisor · Rajyotsava Awardee",
    photo: "/images/ks-nagesh.jpg",
    bio: "A Karnataka Rajyotsava Awardee, Dr. K. S. Nagesh guides Akshara Daana as its Chief Advisor. His decades of public service and deep understanding of Karnataka's social fabric shape the movement's direction — ensuring every school we adopt is approached with wisdom, integrity and a long view.",
  },
  {
    key: "anitha",
    name: "Anitha Dasappa",
    role: "Chief Operating Officer (COO)",
    phone: "+1 (312) 785-8867",
    photo: gd("1lcvLg0w-oYWAlVBpKOShzWFED1TGuT_p"),
    bio: "Environmental Engineer with over 25 years of experience across the government and non-government sectors. A dedicated education advocate committed to advancing equitable opportunities and empowering communities through education. Working alongside founder Mrs. Revathi Kamath, she provides strategic leadership in expanding educational initiatives.",
  },
  {
    key: "dayaprasad",
    name: "Dr. Dayaprasad G Kulkarni",
    role: "Founder & Director · AarogyaSeva and The Billion Hopes Philanthropic Fund",
    photo: gd("16PIcuK1tMJMxsUF5VT0Hx12wUdW_BiMR"),
    bio: "Public health expert and visionary leader dedicated to grassroots social transformation and universal healthcare access across underserved regions.",
  },
  {
    key: "manu",
    name: "Manu Gowda",
    role: "Lead Member, Akshara Daana",
    photo: gd("1en3kGvbBQTgRGmhf10Z9QkgQh4LA1bXj"),
    bio: "Entrepreneur, educationist, environmentalist and social changemaker committed to creating a greener, more sustainable and knowledge-driven society. Actively involved in educational initiatives, lake rejuvenation, afforestation and community development projects.",
  },
  {
    key: "navya",
    name: "Navya Raju",
    role: "Architect & Sustainability",
    photo: gd("1YkUA_8fwi-97c-12U0Vx5tY_qIfCaYQx"),
    bio: "Harvard GSD specialist working on measurable impact and ecological school restoration — bringing sustainable design thinking to every school Akshara Daana adopts.",
  },
  {
    key: "abhinandan",
    name: "Dr. Abhinandan Ballary",
    role: "Joint Director, International Council for AI Development · PR & Media",
    photo: "/images/dr-abhinandan.png",
    bio: "An accomplished academician, writer and leader who has served as Principal and Professor at various institutions, Dr. Abhinandan Ballary currently heads the International Council for AI Development as Joint Director and is associated with United Nations Global Compact outreach programmes. Driven by a deep commitment to education and social transformation, he has joined Akshara Daana to help realise the vision of Smt. Revathi Kamath — turning aspirations into action, empowering underprivileged children through education, and giving wings to young dreams.",
  },
  {
    key: "upendra",
    name: "Mr. Upendra Kumar K R",
    role: "AI Expert · Partner, ODEE",
    photo: "/images/upendra-kumar.jpg",
    bio: "Entrepreneur, technology strategist and innovation leader building technology that solves real-world challenges at scale. Founder & CEO of ODEE (One Destination for Education & Excellence), an AI-powered Education Operating System, and Founder of Zagoo Smart Parking. His belief: \u201cTechnology should not merely automate systems — it should improve lives.\u201d He aims to position India as a global leader in AI-driven, personalized education for every student.",
  },
  {
    key: "rajini",
    name: "Rajini Rekha Yedla",
    role: "Creative Strategy & Social Impact Advisor",
    photo: "/images/rajini-rekha.jpg",
    bio: "A multidisciplinary entrepreneur, artist and art curator, sustainable fashion innovator and social impact advocate. An NIFT alumna and graduate of IIM Bangalore's Women Entrepreneurs Management Programme, she is the Founder-Director of AKAB Innovations, Rêves Art Gallery and Rêves La Veste — integrating innovation, art, sustainable fashion and technology-driven apparel manufacturing. She champions responsible design, Indian heritage and contemporary power dressing, and uses art and design with NGOs and community initiatives to foster awareness, inclusivity and positive social change.",
  },
  {
    key: "alkananda",
    name: "Smt. Alkananda",
    role: "Member",
    photo: "/images/alkananda.jpg",
    bio: "A committed member of the Akshara Daana circle, Smt. Alkananda lends her time and care to the movement — supporting school visits, community outreach and the everyday work that keeps rural classrooms moving forward.",
  },
  {
    key: "subhashini",
    name: "Dr. Subhashini",
    role: "Member",
    photo: "/images/subhashini.jpg",
    bio: "A practising physiotherapist and healthcare professional, Dr. Subhashini brings a caring, clinical eye to Akshara Daana — supporting the health, hygiene and well-being of children in the schools we adopt, and lending her time to school visits and community outreach.",
  },
  {
    key: "surendra",
    name: "Surendra Kuthloor Hegde",
    role: "Advisory Council Member · Entrepreneur & Well-being Catalyst",
    photo: "/images/surendra-hegde.jpg",
    bio: "Co-founder & CEO of E & L Hyperform Pvt. Ltd. and Co-founder of Kimera Wellness Spa Salon. Guided by a philosophy of holistic well-being, sustainability and harmony with nature, he believes in creating meaningful spaces and nurturing healthier communities. At Akshara Daana, he brings this vision to ensuring that every rupee invested in rural schools contributes to safe, sustainable and dignified learning environments where children can thrive.",
  },
  {
    key: "deepti",
    name: "Deepti Ashok",
    role: "Member",
    photo: "/images/deepti-ashok.jpg",
    bio: "A warm and committed member of the Akshara Daana circle, Deepti Ashok supports the movement's school visits, community outreach and events — bringing energy and care to the everyday work of keeping rural classrooms moving forward.",
  },
];

export default function Team() {
  const ref = useReveal();

  return (
    <section id="team" data-testid="team-section" className="relative py-16 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal max-w-3xl text-center md:text-left">
          <span className="section-chip">Our Core Team</span>
          <h2 className="mt-4 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight">
            The Hands Behind the <em>Movement.</em>
          </h2>
          <p className="mt-4 text-navy/75 font-display text-base md:text-lg leading-relaxed">
            A diverse collective of visionary leaders and ground-team members, working closely to build an
            ethical philanthropic movement across rural Karnataka.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEAM.map((m) => (
            <article
              key={m.key}
              data-testid={`team-member-${m.key}`}
              className="manuscript-card !p-0 overflow-hidden flex flex-col group"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-navy/10">
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div data-testid={`team-placeholder-${m.key}`} className="w-full h-full bg-navy flex items-center justify-center">
                    <span className="w-28 h-28 rounded-full bg-teal/20 border-2 border-teal/60 text-teal font-display font-extrabold text-4xl flex items-center justify-center">
                      {m.initials}
                    </span>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy/85 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <h3 className="font-display font-extrabold text-white text-xl leading-tight">
                    {m.name}
                  </h3>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="text-teal font-display font-bold text-xs uppercase tracking-widest">
                  {m.role}
                </div>
                <p className="mt-3 text-navy/75 font-display text-sm leading-relaxed flex-1">
                  {m.bio}
                </p>
                {m.phone && (
                  <div className="mt-4 pt-4 dashed-divider flex items-center gap-2 text-navy/80 font-display text-sm">
                    <Phone className="w-4 h-4 text-teal" />
                    <a href={`tel:${m.phone.replace(/\s+/g, "")}`} className="hover:text-teal">
                      {m.phone}
                    </a>
                  </div>
                )}
              </div>
            </article>
          ))}

          {/* Join CTA card */}
          <article className="bg-navy text-white flex flex-col justify-between p-8 rounded-sm shadow-card">
            <div>
              <div className="w-12 h-12 rounded-full bg-teal/20 border-2 border-teal flex items-center justify-center">
                <Users className="w-6 h-6 text-teal" />
              </div>
              <h3 className="mt-5 font-display font-extrabold text-2xl leading-tight">
                Add your hands to the movement.
              </h3>
              <p className="mt-3 font-display text-white/75 text-sm leading-relaxed">
                Volunteers, field partners, teachers, donors and well-wishers — we welcome every kind of
                contribution to rural education.
              </p>
            </div>
            <Link
              to="/get-involved#volunteer"
              data-testid="team-join-cta"
              className="mt-6 inline-flex items-center gap-2 bg-teal hover:bg-teal-dark transition-colors text-white font-display font-bold text-sm px-5 py-3 rounded-sm w-fit"
            >
              Join our team
              <span aria-hidden>→</span>
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}
